import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Popper from "@mui/material/Popper";
import { Badge, Box, CircularProgress, IconButton, Stack } from "@mui/material";
import {
    MarkEmailRead,
    NotificationsNoneOutlined,
    PersonAddAlt,
    PriorityHigh,
    RemoveRedEye,
} from "@mui/icons-material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import styled from "styled-components";
import formatDistanceToNow from "date-fns/formatDistanceToNow";
import Flex from "src/components/flex";
import googleWorkspaceInt from "src/assets/icons/google-workspace-int";
import Icon from "src/components/icon";
import slackIcon from "src/assets/icons/slack-int";
import gitlabIcon from "src/assets/icons/gitlab-int";
import githubIcon from "src/assets/icons/github-int";
import gitlabSelfManagedIcon from "src/assets/icons/gitlab-selfmanaged-int";
import missionSuccessfulIcon from "src/assets/icons/mission-successful";
import missionFailedIcon from "src/assets/icons/mission-failed";
import Link from "src/components/link";
import jiraIcon from "src/assets/icons/jira-int";
import notionIcon from "src/assets/icons/notion-int";
import microsoftCalendarIcon from "src/assets/icons/microsoft-calendar-int";
import { NotificationContext } from "../providers/notification";
import request from "../../utils/request";

const MenuBoundingBox = styled.div`
    width: 406px;
    left: 1565px;
    top: 111px;
    border: 4px solid #3d3d3d;
    box-sizing: border-box;
    border-radius: 14px;
    background-color: ${({ theme }) => theme.palette.background.default};
`;

const NotificationItemWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    position: relative;
    padding: 12px;
    width: 100%;
`;

const NotificationIconWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 46px;
    height: 46px;
    background-color: #3d3d3d;
    border-radius: 50%;
    margin-right: 16px;
`;

const NotificationContentWrapper = styled.div`
    word-break: break-word;
    font-size: 12px;
`;

const NotificationSubContentWrapper = styled.div`
    word-break: break-word;
    font-size: 11px;
    opacity: 0.8;
`;

const FlexBox = styled.div`
    display: flex;
    align-items: center;
`;

const ErrorText = styled.p`
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.secondary.main};
    margin: 8px;
    text-align: center;
    padding: 0;
`;

const AllText = styled.p`
    font-size: 12px;
    font-weight: 500;
    color: ${({ theme }) => theme.palette.secondary.main};
    margin: 8px;
    padding: 0;
`;

const ActionText = styled.p`
    font-size: 10px;
    font-weight: 300;
    color: ${({ theme }) => theme.palette.secondary.main};
    margin: 8px;
    padding: 0;
    text-align: center;
    cursor: pointer;
    &:hover {
        color: ${({ theme }) => theme.palette.primary.main};
    }
`;

const DateText = styled.span`
    font-size: 10px;
    opacity: 0.5;
`;

const Strong = styled.span`
    font-weight: 600;
`;

const MenuContainer = styled.div`
    & > div {
        border-bottom: solid #3d3d3d 2px;
    }
`;

const NotificationIcon = ({ type = "", data }) => {
    const assignIcon = () => {
        if (type === "alert") {
            return <PriorityHigh />;
        }
        if (type === "add") {
            return <PersonAddAlt />;
        }
        if (type === "mission") {
            if (data.missionStatus === "completed") {
                return <Icon svg={missionSuccessfulIcon} />;
            }
            if (data.missionStatus === "failed") {
                return <Icon svg={missionFailedIcon} />;
            }
        }
        if (type === "integration") {
            if (data.integration === "slack") {
                return <Icon svg={slackIcon} />;
            }
            if (data.integration === "gitlab") {
                return <Icon svg={gitlabIcon} />;
            }
            if (data.integration === "gitlabSelfManaged") {
                return <Icon svg={gitlabSelfManagedIcon} />;
            }
            if (data.integration === "github") {
                return <Icon svg={githubIcon} />;
            }
            if (data.integration === "google") {
                return <Icon svg={googleWorkspaceInt} />;
            }
            if (data.integration === "jira") {
                return <Icon svg={jiraIcon} />;
            }
            if (data.integration === "microsoft") {
                return <Icon svg={microsoftCalendarIcon} />;
            }
            if (data.integration === "notion") {
                return <Icon svg={notionIcon} />;
            }
        }
        if (type === "custom-causal-graph") {
            return <AccountTreeIcon />;
        }
        if (type === "achievement") {
            const isProd = process.env.ENVIRONMENT_NAME === "prod";
            const setAssetLink = () => {
                if (isProd) return "https://assets.adadot.com/achievements";
                return "https://assets-sit.adadot.com/achievements";
            };
            const assetLink = setAssetLink();
            return (
                <Box
                    component="img"
                    sx={{ width: "36px", borderRadius: "40px" }}
                    alt={data.name}
                    src={`${assetLink}/${data.achievementCategory}/${data.achievementId}.png`}
                />
            );
        }
        return <PriorityHigh />;
    };
    const iconToShow = assignIcon();
    return <NotificationIconWrapper>{iconToShow}</NotificationIconWrapper>;
};

const NotificationContent = ({ type, data }) => {
    if (type === "integration") {
        if (data.integrationStatus === "completed")
            return (
                <Stack direction="column">
                    <NotificationContentWrapper>
                        <Strong>
                            {data.integration.charAt(0).toUpperCase() + data.integration.slice(1)}
                        </Strong>{" "}
                        has finished scrapping
                    </NotificationContentWrapper>
                    {(data.integration === "github" ||
                        data.integration === "gitlab" ||
                        data.integration === "gitlabSelfHosted") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections
                        </NotificationSubContentWrapper>
                    )}
                    {(data.integration === "slack" ||
                        data.integration === "google" ||
                        data.integration === "microsoft") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/collaboration-analytics">
                                {" "}
                                Collaboration{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "jira" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "notion" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section
                        </NotificationSubContentWrapper>
                    )}
                </Stack>
            );
        if (data.integrationStatus === "limited-data")
            return (
                <Stack direction="column">
                    <NotificationContentWrapper>
                        <Strong>
                            {data.integration.charAt(0).toUpperCase() + data.integration.slice(1)}
                        </Strong>{" "}
                        has some data!
                    </NotificationContentWrapper>
                    {(data.integration === "github" ||
                        data.integration === "gitlab" ||
                        data.integration === "gitlabSelfHosted") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections
                        </NotificationSubContentWrapper>
                    )}
                    {(data.integration === "slack" ||
                        data.integration === "google" ||
                        data.integration === "microsoft") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/collaboration-analytics">
                                {" "}
                                Collaboration{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "jira" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "notion" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section
                        </NotificationSubContentWrapper>
                    )}
                </Stack>
            );
        if (data.integrationStatus === "scrapping")
            return (
                <Stack direction="column">
                    <NotificationContentWrapper>
                        <Strong>
                            {data.integration.charAt(0).toUpperCase() + data.integration.slice(1)}
                        </Strong>{" "}
                        has some data!
                    </NotificationContentWrapper>
                    {(data.integration === "github" ||
                        data.integration === "gitlab" ||
                        data.integration === "gitlabSelfHosted") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections
                        </NotificationSubContentWrapper>
                    )}
                    {(data.integration === "slack" ||
                        data.integration === "google" ||
                        data.integration === "microsoft") && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/collaboration-analytics">
                                {" "}
                                Collaboration{" "}
                            </Link>
                            and
                            <Link variant="plain" to="/dashboard/wellbeing-analytics">
                                {" "}
                                Wellbeing{" "}
                            </Link>
                            sections
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "jira" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section
                        </NotificationSubContentWrapper>
                    )}
                    {data.integration === "notion" && (
                        <NotificationSubContentWrapper>
                            Check out
                            <Link variant="plain" to="/dashboard/work-analytics">
                                {" "}
                                Work{" "}
                            </Link>
                            section
                        </NotificationSubContentWrapper>
                    )}
                </Stack>
            );
        if (data.integrationStatus === "inactive")
            return (
                <Stack direction="column">
                    <NotificationContentWrapper>
                        <Strong>
                            {data.integration.charAt(0).toUpperCase() + data.integration.slice(1)}
                        </Strong>{" "}
                        is inactive
                    </NotificationContentWrapper>
                    <NotificationSubContentWrapper>{data.details}</NotificationSubContentWrapper>
                </Stack>
            );
    }
    if (type === "add") {
        return (
            <NotificationContentWrapper>
                <Strong>{data.user}</Strong> has added you to <Strong>{data.team}</Strong>
            </NotificationContentWrapper>
        );
    }
    if (type === "custom-causal-graph") {
        return (
            <Stack direction="column">
                <NotificationContentWrapper>
                    Your <Strong> personalised Causal AI model</Strong> has been calculated.
                </NotificationContentWrapper>
                <NotificationSubContentWrapper style={{ fontWeight: 200 }}>
                    {data.additionalInfo}
                </NotificationSubContentWrapper>
            </Stack>
        );
    }
    if (type === "achievement") {
        return (
            <NotificationContentWrapper>
                <Link variant="plain" to="/achievements">
                    New achievement <Strong>{data.achievementName}</Strong>
                </Link>
            </NotificationContentWrapper>
        );
    }
    if (type === "mission") {
        if (data && !data.userId) {
            return (
                <NotificationContentWrapper>
                    <Link variant="plain" to={`/teams/${data.teamId}`}>
                        Team {data.teamName} {data.missionStatus}{" "}
                        <Strong>{data.missionName}</Strong>
                    </Link>
                </NotificationContentWrapper>
            );
        }
        return (
            <NotificationContentWrapper>
                <Link variant="plain" to={`/teams/${data.teamId}`}>
                    Mission <Strong>{data.missionName}</Strong> {data.missionStatus}
                </Link>
            </NotificationContentWrapper>
        );
    }
    return <div>No Content</div>;
};

const NotificationItem = ({ notification }) => {
    const notificationContext = useContext(NotificationContext);
    const [loading, setLoading] = useState(false);
    const { removeNotification } = notificationContext;

    const handleDeleteItem = async () => {
        try {
            setLoading(true);
            await request("/notifications-api/update", {
                method: "POST",
                body: JSON.stringify({ id: notification.id }),
            });
            removeNotification(notification);
        } catch (e) {
            console.log(e);
            setLoading(false);
        }
    };

    return (
        <NotificationItemWrapper>
            <FlexBox>
                <NotificationIcon type={notification.type} data={notification.data} />
                <Stack direction="column">
                    <NotificationContent type={notification.type} data={notification.data} />
                    <DateText>
                        {formatDistanceToNow(notification.createdAt, { addSuffix: true })}
                    </DateText>
                </Stack>
            </FlexBox>
            <IconButton size="small" onClick={() => handleDeleteItem()}>
                {loading ? <CircularProgress size={20} /> : <RemoveRedEye />}
            </IconButton>
        </NotificationItemWrapper>
    );
};

const NotificationMenu = () => {
    const notificationContext = useContext(NotificationContext);
    const { notifications } = notificationContext;
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (open === true) {
            anchorRef.current.blur();
        }
    }, [open]);

    const handleToggle = () => {
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    };

    const handleMarkAsRead = () => {
        const { removeNotification } = notificationContext;
        notifications.forEach(async (entry) => {
            try {
                await request("/notifications-api/update", {
                    method: "POST",
                    body: JSON.stringify({ id: entry.id }),
                });
                removeNotification(entry);
            } catch (e) {
                console.log(e);
            }
        });
    };

    const handleNavigate = () => {
        setOpen((status) => !status);
        navigate("/notifications");
    };

    return (
        <Flex data-testid="header-notification-menu">
            <div>
                <IconButton
                    ref={anchorRef}
                    color="secondary"
                    size="large"
                    onClick={handleToggle}
                    aria-label="Notifications"
                >
                    {notifications && notifications.length ? (
                        <Badge badgeContent={notifications.length} max={20} color="secondary">
                            <NotificationsNoneOutlined />
                        </Badge>
                    ) : (
                        <NotificationsNoneOutlined />
                    )}
                </IconButton>

                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    role={undefined}
                    style={{ zIndex: 3, wordBreak: "break-all" }}
                    transition
                    disablePortal
                    placement="bottom-end"
                >
                    {({ TransitionProps }) => {
                        return (
                            <Grow {...TransitionProps} style={{ transformOrigin: "right top" }}>
                                <MenuBoundingBox>
                                    <ClickAwayListener onClickAway={handleClose}>
                                        <MenuContainer>
                                            <Stack
                                                direction="row"
                                                justifyContent="space-between"
                                                alignItems="center"
                                                style={{ width: "100%" }}
                                            >
                                                <AllText>New Notifications</AllText>
                                                <ActionText onClick={handleMarkAsRead}>
                                                    Mark all as read
                                                </ActionText>
                                            </Stack>
                                            <Box
                                                style={{
                                                    maxHeight: "450px",
                                                    overflow: "auto",
                                                }}
                                            >
                                                {notifications && notifications.length ? (
                                                    notifications.map((notification) => (
                                                        <NotificationItem
                                                            key={notification.id}
                                                            notification={notification}
                                                        />
                                                    ))
                                                ) : (
                                                    <Stack
                                                        direction="column"
                                                        alignItems="center"
                                                        mt={2.5}
                                                        mb={2}
                                                        style={{ opacity: "0.3" }}
                                                    >
                                                        <MarkEmailRead />
                                                        <ErrorText>All caught up!</ErrorText>
                                                    </Stack>
                                                )}
                                            </Box>
                                            <ActionText onClick={handleNavigate}>
                                                View All Notifications
                                            </ActionText>
                                        </MenuContainer>
                                    </ClickAwayListener>
                                </MenuBoundingBox>
                            </Grow>
                        );
                    }}
                </Popper>
            </div>
        </Flex>
    );
};

export default NotificationMenu;
